'use-client';
import * as React from 'react';
import Link from '@wix/thunderbolt-elements/src/components/Link/viewer/Link';
import {
  formatClassNames,
  getDataAttributes,
  isEmptyObject,
} from '@wix/editor-elements-common-utils';
import type { SvgImageProps } from '../SvgImage.types';
import styles from './SvgImage.module.scss';
import classNames from 'clsx';
import semanticClassNames from '../SvgImage.semanticClassNames';
import { isFallbackSvg } from '@wix/editor-elements-corvid-utils';

const ConditionalLink = ({
  link,
  children,
}: React.PropsWithChildren<Pick<SvgImageProps, 'link'>>) => {
  const hasLink = !isEmptyObject(link);

  if (!hasLink) {
    return <>{children}</>;
  }

  return <Link {...link}>{children}</Link>;
};

export const SvgImageBase = ({
  id,
  link,
  alt = '',
  svgString,
  svgType,
  bBox,
  viewBox,
  className,
  children,
  displayMode = 'fit',
  customClassNames = [],
  ...props
}: React.PropsWithChildren<SvgImageProps>) => {
  const shouldRenderFallbackSVG = isFallbackSvg(svgString);
  const keepAspectRatio = displayMode === 'fit';

  return (
    <div
      id={id}
      {...getDataAttributes(props)}
      className={classNames(
        formatClassNames(
          semanticClassNames.root,
          ...customClassNames,
          className ?? '',
        ),
      )}
    >
      <ConditionalLink link={link}>
        <div
          data-testid={`svgRoot-${id}`}
          className={classNames(styles.svgImageRoot, styles.svgImageSource)}
        >
          <svg
            aria-label={alt}
            role={alt ? 'img' : 'presentation'}
            aria-hidden={alt ? undefined : true}
            preserveAspectRatio={keepAspectRatio ? 'xMidYMid meet' : 'none'}
            data-type={svgType}
            viewBox={viewBox}
            xmlnsXlink="http://www.w3.org/1999/xlink"
            xmlns="http://www.w3.org/2000/svg"
            data-bbox={bBox}
            data-failed={shouldRenderFallbackSVG ? '' : undefined}
          >
            {!shouldRenderFallbackSVG && svgString && (
              <g
                className={styles.svgImageSource}
                data-type={svgType}
                dangerouslySetInnerHTML={{ __html: svgString }}
              />
            )}
            {children}
          </svg>
        </div>
      </ConditionalLink>
    </div>
  );
};

export default SvgImageBase;
